import './styles.scss'
import 'bootstrap'

import { initSubmitForm } from './components/SubmitForm'
import { ReportForm } from './components/ReportForm'

// Component interface for initialization pattern
interface Component {
    init(): void;
}

class ComponentLoader {
    private readonly components: Map<string, () => Component | null> = new Map();

    register(selector: string, factory: () => Component | null): void {
        this.components.set(selector, factory);
    }

    initAll(): void {
        this.components.forEach((factory, selector) => {
            const component = factory();
            if (component) {
                component.init();
            }
        });
    }
}

// Courier Checkbox Selector Component
class CourierCheckboxSelector implements Component {
    init(): void {
        const container = document.querySelector('[data-js="courier-checkbox-selector"]');
        if (!container) return;

        const selectAllCheckbox = container.querySelector<HTMLInputElement>('[data-js-inner="select-all-couriers"]');
        const courierCheckboxes = container.querySelectorAll<HTMLInputElement>('[data-js-inner="courier-checkbox"]');
        const dropdownButton = container.querySelector<HTMLButtonElement>('[data-js-inner="courier-dropdown-button"]');
        const dropdownMenu = container.querySelector<HTMLElement>('[data-js-inner="dropdown-menu"]');
        
        if (!selectAllCheckbox || courierCheckboxes.length === 0) return;

        // Prevent dropdown from closing when clicking anywhere inside the menu (including whitespace)
        if (dropdownMenu) {
            dropdownMenu.addEventListener('click', (e) => {
                e.stopPropagation();
            });
        }

        const updateButtonText = () => {
            if (!dropdownButton) return;
            
            const checkedCount = Array.from(courierCheckboxes).filter(cb => cb.checked).length;
            const totalCount = courierCheckboxes.length;
            
            if (checkedCount === totalCount) {
                dropdownButton.textContent = 'All Couriers';
            } else if (checkedCount === 0) {
                dropdownButton.textContent = 'Select Couriers';
            } else {
                dropdownButton.textContent = `${checkedCount} Courier${checkedCount === 1 ? '' : 's'} Selected`;
            }
        };

        // Handle "Select All" checkbox change
        selectAllCheckbox.addEventListener('change', () => {
            courierCheckboxes.forEach(checkbox => {
                checkbox.checked = selectAllCheckbox.checked;
            });
            updateButtonText();
        });
        
        // Handle individual courier checkbox changes
        courierCheckboxes.forEach(checkbox => {
            checkbox.addEventListener('change', () => {
                const allChecked = Array.from(courierCheckboxes).every(cb => cb.checked);
                selectAllCheckbox.checked = allChecked;
                updateButtonText();
            });
        });

        // Initialize button text on load
        updateButtonText();
    }
}

// Submit Form Component
class SubmitFormComponent implements Component {
    init(): void {
        const container = document.getElementById('submit-form-container');
        if (container) {
            const recaptchaSiteKey = container.dataset.recaptchaSiteKey;
            
            // Parse initial values if present
            let initialValues: any;
            const valuesJson = container.dataset.initialValues;
            if (valuesJson && valuesJson.trim().length > 0) {
                let trimmedJson = valuesJson.trim();
                
                // Decode HTML entities if present (e.g., &quot; -> ")
                const textarea = document.createElement('textarea');
                textarea.innerHTML = trimmedJson;
                trimmedJson = textarea.value;
                
                // Only try to parse if it looks like valid JSON
                if (trimmedJson.startsWith('{') && trimmedJson.endsWith('}') && trimmedJson.length > 2) {
                    try {
                        initialValues = JSON.parse(trimmedJson);
                        // Ensure selectedRetailerCourierIds is an array
                        if (!Array.isArray(initialValues.selectedRetailerCourierIds)) {
                            initialValues.selectedRetailerCourierIds = [];
                        }
                        // Convert boolean strings to booleans
                        if (typeof initialValues.isNewCourier === 'string') {
                            initialValues.isNewCourier = initialValues.isNewCourier === 'true';
                        }
                    } catch (e) {
                        console.error('Error parsing initial values:', e, 'JSON length:', trimmedJson.length, 'First 100 chars:', trimmedJson.substring(0, 100));
                        initialValues = undefined;
                    }
                } else if (trimmedJson.length > 0) {
                    console.warn('Initial values JSON does not appear to be valid JSON. Length:', trimmedJson.length, 'First 50 chars:', trimmedJson.substring(0, 50));
                }
            }
            
            // Parse initial errors if present
            let initialErrors: { [key: string]: string[] } | undefined;
            const errorsJson = container.dataset.initialErrors;
            if (errorsJson && errorsJson.trim().length > 0) {
                let trimmedJson = errorsJson.trim();
                
                // Decode HTML entities if present (e.g., &quot; -> ")
                const textarea = document.createElement('textarea');
                textarea.innerHTML = trimmedJson;
                trimmedJson = textarea.value;
                
                // Only try to parse if it looks like valid JSON
                if (trimmedJson.startsWith('{') && trimmedJson.endsWith('}')) {
                    try {
                        initialErrors = JSON.parse(trimmedJson);
                    } catch (e) {
                        console.error('Error parsing initial errors:', e, 'JSON length:', trimmedJson.length, 'First 100 chars:', trimmedJson.substring(0, 100));
                    }
                }
            }
            
            if (recaptchaSiteKey) {
                initSubmitForm(container, {
                    recaptchaSiteKey,
                    initialValues,
                    initialErrors
                });
            }
        }
    }
}

// Initialize component loader and register components
const loader = new ComponentLoader();

// Register Courier Checkbox Selector component
loader.register('[data-js="courier-checkbox-selector"]', () => {
    const container = document.querySelector('[data-js="courier-checkbox-selector"]');
    return container ? new CourierCheckboxSelector() : null;
});

// Register Submit Form component
loader.register('#submit-form-container', () => {
    const container = document.getElementById('submit-form-container');
    return container ? new SubmitFormComponent() : null;
});

// Report Form Component
class ReportFormComponent implements Component {
    init(): void {
        const container = document.querySelector('[data-js="report-form"]');
        if (container) {
            new ReportForm(container as HTMLElement);
        }
    }
}

// Register Report Form component
loader.register('[data-js="report-form"]', () => {
    const container = document.querySelector('[data-js="report-form"]');
    return container ? new ReportFormComponent() : null;
});

// Add js-enabled class to body to show JavaScript-dependent elements
document.documentElement.classList.add('js-enabled');

// Load all components when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    loader.initAll();
});